import { Component, OnDestroy, OnInit, ViewChild, inject } from '@angular/core';
import { DOCUMENT } from '@angular/common';
import moment from 'moment';
import { concat, forkJoin, Observable, Subject, Subscription, from, of } from 'rxjs';
import { debounceTime, distinctUntilChanged, map, switchMap } from 'rxjs/operators';

import { PortalSettingsService } from '@core/services/portal-settings.service';
import { UtilityService } from '@core/services/utility.service';
import { AccountService } from '@pages/account/services/account.service';
import { AccountApiService } from '@pages/account/services/account.api.service';
import { EmployeeService } from '@pages/employee/services/employee.service';
import { HomeService } from '@pages/home/services/home.service';
import { ClockInOutService } from '@core/services/clock-in-out.service';
import { LineType } from '@shared/model/line-type.model';
import { SimpleLookupResult } from '@typeDefs/general/general';
import { MechanicFormData } from '@shared/model/mechanic-type-data.model';
import { ClockAction_Request } from '@typeDefs/employee/requests/employee';

@Component({
  selector: 'app-clock-in-out-unlocked',
  templateUrl: './clock-in-out-unlocked.component.html',
  styleUrls: ['./clock-in-out-unlocked.component.css'],
})
export class ClockInOutUnlockedComponent implements OnInit, OnDestroy {
  @ViewChild('signaturePad', { static: false }) signaturePad;

  private readonly document = inject(DOCUMENT);
  private readonly window = this.document.defaultView!;
  private readonly portalSettingsService = inject(PortalSettingsService);
  private readonly utilityService = inject(UtilityService);
  private readonly employeeService = inject(EmployeeService);
  private readonly accountService = inject(AccountService);
  private readonly homeService = inject(HomeService);
  private readonly clockinoutService = inject(ClockInOutService);
  private readonly accountApiService = inject(AccountApiService);

  // Public properties (used in template)
  portalSettings = this.portalSettingsService.getPortalSettings();
  employeeInfo: any;
  clockStatus: boolean = null;
  lastClockDisplay: string;
  formData: any = {};
  customMsg = this.portalSettings.CustomVerbiageClockOutSign.ConfigValue;
  changePhaseNow = false;
  requirePasswordClockIO = this.portalSettings.RequirePasswordClockIO.ConfigValue == '1';
  geolocationEnabled = false;
  requestingLocation = false;
  position: any = null;
  performingClockAction = false;
  companyLoginChoices = this.portalSettings.CompanyLoginChoices.ConfigValue == '1';
  phases: any[] = [];
  lastFullClockIn: any = null;
  lastFullClockOut: any = null;
  lastClockIn: any = null;
  totalHours: number = null;
  alert: any = null;
  loadingAccess = false;
  loading = false;
  employeeName: string;
  requireSign = this.portalSettings.RequireSignClockOut.ConfigValue == '1';
  redBorder = false;
  lastAction = false;
  pageUnlocked = false;
  companies: any[] = [];
  job$: Observable<any>;
  jobInput$ = new Subject<any>();
  panelWidth: number;
  signaturePadOptions: Object = {
    canvasWidth: 300,
    canvasHeight: 150,
  };
  lineTypes: LineType[] = [];
  scopeList: any[] = [];
  payTypeList: any[] = [];
  workOrderList: any[] = [];
  enabledLineTypesInClockIn = this.portalSettings.EnabledLineTypesInClockIn.ConfigValue;
  readyForClocking: boolean = null;
  mealBreak = false;
  mealBreakSetting: { [key: string]: string } = {};
  mealBreakOptions: SimpleLookupResult[] = [
    { ComboType: null, DatabaseValue: 'N', DisplayValue: 'No' },
    { ComboType: null, DatabaseValue: 'Y', DisplayValue: 'Yes' },
  ];
  enableEquipmentLookup = this.portalSettings.ClockInAddEquipment?.ConfigValue == '1';
  equipmentInput$ = new Subject<string>();
  equipmentList: any[];

  // Private properties (not used in template)
  private dateFormat = this.utilityService.getDateFormat();
  private clockMoment: moment.Moment;
  private employeeResults: any = {};
  private tCClockIOIgnoreJobPhase = this.portalSettings.TCClockIOIgnoreJobPhase.ConfigValue == '1';
  private lastUsedJCJPKeyID: any = null;
  private lastUsedJCJMKeyID: any = null;
  private jobs: any[] = [];
  private accessToken: any;
  private signatureData: any;
  private signatureNEmpty = false;
  private timeFormat = ' hh:mm a';
  private licensedUser: any = {};
  private lastClockinType: any;
  private hadMealBreakToday = false;
  private kioskLoginSubscription: Subscription;

  equipment$ = this.equipmentInput$.pipe(
    debounceTime(500),
    distinctUntilChanged(),
    switchMap(term =>
      term?.length > 2
        ? this.employeeService
            .getEquipments(
              term,
              null,
              null,
              null,
              this.formData.unlockPass
            )
            .pipe(
              map(response =>
                response.map(eq => ({
                  ...eq,
                  DisplayValue: `${eq.EMCo} - ${eq.Equipment} - ${eq.Description}`,
                }))
              )
            )
        : of([])
    )
  );

  constructor() {}

  ngOnInit(): void {
    this.window.history.pushState(null, null, '/employee/clockinoutunlocked');
    const width = this.window.innerWidth - 80;
    this.panelWidth = (width > 300 ? 300 : width) + 40;
    this.formData.labelFade = false;
    this.formData.selectedJob = null;
    this.formData.selectedPhase = null;
    this.formData.lineType = null;
    this.formData.workOrder = null;
    this.formData.scope = null;
    this.formData.payType = null;
    this.clockinoutService.resetMechanicFormData(this.formData);
    if (sessionStorage) {
      this.getLocation();
    }
    if (this.companyLoginChoices) {
      this.getCompanies();
    }
    this.registerLookups();
    this.getMealBreakSetting();
  }

  ngOnDestroy(): void {
    this.jobInput$.unsubscribe();
    this.equipmentInput$.unsubscribe();
    this.kioskLoginSubscription?.unsubscribe();
  }

  lockPage(): void {
    this.window.location.href = '/Account/Logout';
  }

  getMealBreakSetting(): void {
    if (this.portalSettings.ClockInMealBreakMinutes.ConfigValue != null) {
      this.portalSettings.ClockInMealBreakMinutes.ConfigValue.split(';').forEach((val: string) => {
        if (val) {
          const settingParts = val.split(',');
          this.mealBreakSetting[settingParts[0] + ',' + settingParts[1]] = settingParts[2];
        }
      });
    }
  }

  showMealBreak(): boolean {
    if (this.employeeInfo != null) {
      return this.mealBreakSetting[this.employeeInfo.PRCo + ',' + this.employeeInfo.PRGroup] != null && !this.hadMealBreakToday;
    } else return false;
  }

  async getClockInStatus(): Promise<void> {
    this.lastFullClockIn = null;
    this.lastFullClockOut = null;
    this.lastClockIn = null;
    this.totalHours = null;
    this.alert = null;
    this.phases = [];
    this.lineTypes = [];
    this.readyForClocking = false;
    if (!this.requirePasswordClockIO) {
      await this.loadUserDetails();
      await this.fetchClockInStatus(null);
    } else {
      if (this.formData.Employee == null || this.formData.Password == null || this.formData.Password == '') {
        this.alert = { type: 'danger', msg: 'Please enter your Employee # and Password.' };
        return;
      }
      this.formData.PMMode = false;
      this.loadingAccess = true;
      this.kioskLoginSubscription = this.accountApiService
        .kioskLogin(this.formData.PRCo, this.formData.Employee, this.formData.Password)
        .pipe(
          switchMap(response => {
            if (!response.success) {
              this.alert = { type: 'danger', msg: response.error || 'Login failed.' };
              this.loadingAccess = false;
              return [];
            }
            return from(this.loadUserDetails()).pipe(
              switchMap(() => from(this.accountService.getJobsAccessToken())),
              switchMap(tokenResponse => {
                if (tokenResponse != null && tokenResponse[0] != null) {
                  this.accessToken = tokenResponse[0];
                  return from(this.fetchClockInStatus(this.accessToken));
                }
                return [];
              })
            );
          })
        )
        .subscribe({
          error: err => {
            this.alert = { type: 'danger', msg: err ?? 'An error occurred during login. Please try again.' };
            this.loadingAccess = false;
          },
        });
    }
  }

  async fetchClockInStatus(accessToken?: any): Promise<void> {
    const currentTimeString = moment().format('MM/DD/YYYY h:mm:ss a');
    const requestParam =
      'pRCo=' +
      this.formData.PRCo +
      '&employee=' +
      this.formData.Employee +
      '&fromFieldKiosk=true&currentTimeString=' +
      currentTimeString +
      (accessToken ? '&jobAccessToken=' + accessToken : '') +
      '&kioskPass=' +
      (encodeURIComponent(this.formData.unlockPass) ?? null);
    const clockInResponse: any = await this.employeeService.getClockInStatus(requestParam);
    this.readyForClocking = true;
    if (clockInResponse != null) {
      this.lastClockinType = clockInResponse.LastUsedClockinType ?? 'J';
      this.loadingAccess = false;
      this.loadClockInType();

      this.setClockStatusResponse(clockInResponse);
    }
  }

  async loadUserDetails(): Promise<void> {
    const requirePasswordClockIO = this.requirePasswordClockIO;
    const { licensedUser, employeeInfo } = await forkJoin({
      licensedUser: this.homeService.getLicenseCounts(this.formData.PRCo, this.formData.Employee),
      employeeInfo: requirePasswordClockIO
        ? this.homeService.getEmployeeInfo()
        : this.employeeService.getEmployeeInfoAnon(this.formData.PRCo, this.formData.Employee, this.formData.unlockPass),
    }).toPromise();
    this.licensedUser = licensedUser;
    this.employeeInfo = employeeInfo;
  }

  cancelClockIO(): void {
    this.alert = null;
    this.accountService.getAccountLogoutRedirect().then((response: any) => {
      this.resetForm();
    });
  }

  changePhase(): void {
    this.alert = null;
    this.changePhaseNow = true;
  }

  cancelChangePhase(): void {
    this.changePhaseNow = false;
  }

  confirmChangePhase(ignoreLocation: boolean): void {
    if (this.requireSign) {
      this.checkSigned();
    }
    if (!this.redBorder) {
      this.performingClockAction = true;
      const requestObj = this.createChangePhaseRequestObject(ignoreLocation);
      this.employeeService.postClockActionAnon(requestObj).then((response: any) => {
        if (response != null) {
          this.requestingLocation = false;
          this.formData.action = null;
          this.formData.actionMode = null;
          this.signatureNEmpty = false;
          if (response.success) {
            if (this.formData.lineType == 'J') {
              requestObj.JCJMKeyID = this.formData.selectedJob.JCJMKeyID;
              requestObj.JCJPKeyID = this.formData.selectedPhase.JCJPKeyID;
              requestObj.EquipmentKeyID = this.enableEquipmentLookup ? this.formData.selectedEquipment?.KeyID : null;
            }
            this.employeeService.postClockActionAnon(requestObj).then((clockResponse: any) => {
              if (clockResponse != null) {
                this.showBanner(clockResponse.resultClockAction);
                this.cancelChangePhase();
              }
            });
            this.alert = null;
          } else {
            this.alert = { type: 'danger', msg: response.error };
            this.performingClockAction = false;
            this.cancelChangePhase();
            this.resetForm();
          }
        }
      });
    }
  }

  resetGetStatus(): void {
    if (this.clockStatus != null && this.requirePasswordClockIO) {
      this.accountService.getAccountLogoutRedirect();
    }
    this.clockStatus = null;
    this.mealBreak = false;
  }

  postClockAction(ignoreLocation: boolean): void {
    this.performingClockAction = true;
    const requestObj = this.createRequestObject(ignoreLocation);
    this.employeeService.postClockActionAnon(requestObj).then((response: any) => {
      if (response != null) {
        this.requestingLocation = false;
        this.formData.action = null;
        this.formData.actionMode = null;
        this.signatureNEmpty = false;
        if (response.success) {
          this.showBanner(response.resultClockAction);
        } else {
          this.alert = { type: 'danger', msg: response.error };
          this.performingClockAction = false;
        }
      }
    });
  }

  createChangePhaseRequestObject(ignoreLocation: boolean): ClockAction_Request {
    const requestObj: ClockAction_Request = {
      PRCo: this.formData.PRCo,
      Employee: this.formData.Employee,
      FromGroupClock: false,
      GPSLat: null,
      GPSLong: null,
      ClockTimeString: null,
      KioskPass: null,
      JCJMKeyID: null,
      JCJPKeyID: null,
      MealBreak: this.formData.mealBreak == 'Y',
      SignatureData: this.signatureNEmpty ? this.signatureData : null,
      SMPayType: null,
    };
    if (!ignoreLocation) {
      requestObj.GPSLat = this.position.coords.latitude;
      requestObj.GPSLong = this.position.coords.longitude;
    }
    const currentTime = moment();
    requestObj.ClockTimeString = currentTime.format('MM/DD/YYYY h:mm:ss a');
    requestObj.KioskPass = this.formData.unlockPass ?? null;
    return requestObj;
  }

  createRequestObject(ignoreLocation: boolean): ClockAction_Request {
    const requestObj: ClockAction_Request = {
      PRCo: this.formData.PRCo,
      Employee: this.formData.Employee,
      GPSLat: null,
      GPSLong: null,
      JCJMKeyID: null,
      JCJPKeyID: null,
      ClockTimeString: null,
      KioskPass: null,
      SMWorkOrderScopeID: null,
      SMPayType: null,
      SMTripID: null,
      MealBreak: this.formData.mealBreak == 'Y',
      EMWorkOrderKeyID: null,
      EMWorkOrderItemKeyID: null,
      EquipmentKeyID: null,
      EMCostCodeKeyID: null,
      FromGroupClock: false,
      SignatureData: this.signatureNEmpty ? this.signatureData : null,
    };
    if (!ignoreLocation) {
      requestObj.GPSLat = this.position.coords.latitude;
      requestObj.GPSLong = this.position.coords.longitude;
    }
    if (this.formData.lineType == 'J' && !this.clockStatus && this.formData.selectedJob != null && this.formData.selectedPhase != null) {
      requestObj.JCJMKeyID = this.formData.selectedJob.JCJMKeyID;
      requestObj.JCJPKeyID = this.formData.selectedPhase.JCJPKeyID;
      if(this.enableEquipmentLookup){
        requestObj.EquipmentKeyID = this.formData.selectedEquipment?.KeyID;
      }
    } else if (this.formData.lineType == 'S') {
      requestObj.SMTripID = this.formData.workOrder?.SMTripID;
      requestObj.SMWorkOrderScopeID = this.formData.scope?.SMWorkOrderScopeID;
      requestObj.SMPayType = this.formData.payType?.DatabaseValue;
    } else if (this.formData.lineType == 'M') {
      requestObj.EMWorkOrderKeyID = this.formData.selectedEMWorkOrder?.KeyID;
      requestObj.EMWorkOrderItemKeyID = this.formData.selectedEMWorkOrderItem?.KeyID;
      requestObj.EquipmentKeyID = this.formData.selectedEquipment?.KeyID;
      requestObj.EMCostCodeKeyID = this.formData.selectedEMCostCode?.KeyID;
    }
    const currentTime = moment();
    requestObj.ClockTimeString = currentTime.format('MM/DD/YYYY h:mm:ss a');
    requestObj.KioskPass = this.formData.unlockPass;
    return requestObj;
  }

  unlockForm(): void {
    this.employeeService.unlockClockInPage(this.formData.unlockPass).then((response: any) => {
      if (response != null) {
        if (response.success) {
          this.alert = null;
          this.pageUnlocked = true;
        } else {
          this.alert = { type: 'danger', msg: 'The unlock password was incorrect.' };
        }
      }
    });
  }

  jobChanged(): void {
    this.formData.selectedPhase = null;
    this.phases = [];
    if (this.formData.selectedJob != null) {
      this.getPhases();
    }
  }

  loadPhase(): void {
    if (
      (this.phases == null || this.phases.length <= 0) &&
      this.formData.selectedJob != null &&
      this.formData.selectedJob.JCJMKeyID != null
    ) {
      this.getPhases();
    }
  }

  closeAlert(): void {
    this.alert = null;
  }

  clockOutClicked(location: any): void {
    if (this.requireSign) {
      this.checkSigned();
      if (this.signatureNEmpty) {
        this.signatureData = this.signaturePad.toDataURL().replace(/^data:image\/(png|jpg);base64,/, '');
        this.postClockAction(location);
      }
    }
  }

  clear(): void {
    if (this.signaturePad != null) {
      this.signaturePad.clear();
      this.signatureNEmpty = false;
    }
  }

  searchPhases(term: string, item: any): boolean {
    term = term.trim().toLowerCase();
    return (
      item.JCJPKeyID?.toString().indexOf(term) > -1 ||
      item.PhaseDescription?.toLowerCase().indexOf(term) > -1 ||
      item.PhaseGroup?.toString().indexOf(term) > -1
    );
  }

  async getWorkOrder(): Promise<void> {
    this.workOrderList = [];
    const response = await this.employeeService.getWorkOrderTripsByEmployeeAnon(
      this.formData.PRCo,
      this.formData.Employee,
      this.formData.unlockPass
    );
    if (response != null && Array.isArray(response)) {
      this.workOrderList = response.map(val => ({
        ...val,
        ScheduledDateDisplay: val.ScheduledDate != null ? moment(val.ScheduledDate).utc().format('DD/MMM') : '',
        Display: `${val.ScheduledDate != null ? moment(val.ScheduledDate).utc().format('DD/MMM') + ' - ' : ''}WO #${val.WorkOrder} ${val.TripDescription}`,
      }));
    }
  }

  workOrderChanged(): void {
    this.formData.scope = null;
    this.formData.payType = null;
    if (this.formData.workOrder != null) {
      this.getScope();
      this.getPayType();
    } else {
      this.scopeList = [];
      this.payTypeList = [];
    }
  }

  async getScope(): Promise<void> {
    this.scopeList = [];
    const response = await this.employeeService.getWorkOrderScopesAnon(
      this.formData.workOrder.SMWorkOrderID,
      this.formData.unlockPass
    );
    if (response != null && Array.isArray(response)) {
      this.scopeList = response;
      this.scopeList = response.map(val => ({
        ...val,
        Display: val.Scope + ' - ' + val.ScopeDetails,
      }));
    }
  }

  async getPayType(): Promise<void> {
    this.payTypeList = [];
    if (this.formData.workOrder.SMCo != null) {
      const response = await this.employeeService.getSMPayTypesAnon(
        this.formData.workOrder.SMCo,
        this.formData.unlockPass
      );
      if (response != null && Array.isArray(response)) {
        this.payTypeList = response.map(val => ({
          ...val,
          Display: val.DatabaseValue + ' - ' + val.DisplayValue,
        }));
      }
    }
  }

  typeChanged(): void {
    if (this.formData.lineType == 'J') {
      this.formData.selectedJob = null;
      this.formData.selectedPhase = null;
      this.phases = [];
    } else if (this.formData.lineType == 'S') {
      this.formData.workOrder = null;
      this.formData.scope = null;
      this.formData.payType = null;
      this.scopeList = [];
      this.payTypeList = [];
      this.getWorkOrder();
    } else {
      this.clockinoutService.resetMechanicFormData(this.formData);
    }
  }

  private loadClockInType(): void {
    this.lineTypes = this.clockinoutService.getEnabledLineTypes(this.licensedUser, this.employeeInfo.IsActiveTechnician);
    if (this.lineTypes.length > 0) {
      this.formData.lineType =
        this.lineTypes.length == 1 || !this.lineTypes.some(lineType => lineType.Value == this.lastClockinType)
          ? this.lineTypes[0].Value
          : this.lastClockinType;

      if (this.formData.lineType == 'S') this.getWorkOrder();
    }
  }

  private registerLookups() {
    this.job$ = concat(
      this.jobInput$.pipe(
        debounceTime(500),
        distinctUntilChanged(),
        switchMap(async (term: any) => {
          if (term != null && term.length > 2) {
            const result: any = await this.employeeService.getJobsForClockIn(
              this.formData.PRCo,
              this.formData.Employee,
              term,
              this.accessToken,
              encodeURIComponent(this.formData.unlockPass) ?? null
            );
            result.forEach((val: any) => {
              val.JobDisplayName = val.JobDisplayName + ' (' + 'JCCo - ' + val.JCCo + ')';
              if (this.lastUsedJCJMKeyID != null && val.JCJMKeyID == this.lastUsedJCJMKeyID) {
                this.formData.selectedJob = val;
              }
            });
            if (this.formData.selectedJob != null) {
              this.jobChanged();
            } else {
              this.phases = [{ JCJPKeyID: null, Phase: '', PhaseDescription: 'No Phase', PhaseGroup: null }];
              this.formData.selectedPhase = this.phases[0];
            }
            return result;
          }
        })
      )
    );
    
    this.equipment$.subscribe(equipmentList => this.equipmentList = equipmentList);
  }

  private getPREHKeyIDByEmployee() {
    this.loading = true;
    this.employeeService
      .getPREHKeyIDByEmployee(this.formData.PRCo, this.formData.Employee, encodeURIComponent(this.formData.unlockPass) ?? null)
      .then((response: any) => {
        this.employeeResults = response;
        this.employeeName = this.employeeResults != null ? this.employeeResults.FirstName + ' ' + this.employeeResults.LastName : null;
        this.employeeService
          .getClockHistory(this.formData.PRCo, this.formData.Employee, encodeURIComponent(this.formData.unlockPass) ?? null)
          .then((historyResponse: any) => {
            this.lastFullClockIn = null;
            this.lastFullClockOut = null;
            this.lastClockIn = null;
            this.totalHours = null;
            if (historyResponse != null && historyResponse.length > 0) {
              if (!historyResponse[historyResponse.length - 1].ClockAction) {
                //not clocked in
                var reversedResponse = historyResponse.slice(-2).reverse();
                this.lastFullClockOut = reversedResponse[0];
                this.lastFullClockOut.ClockTimeDisplay = moment(this.lastFullClockOut.ClockTime)
                  .utc()
                  .format(this.dateFormat.toUpperCase() + this.timeFormat);
                this.lastFullClockIn = reversedResponse[1];
                this.lastFullClockIn.ClockTimeDisplay = moment(this.lastFullClockIn.ClockTime)
                  .utc()
                  .format(this.dateFormat.toUpperCase() + this.timeFormat);
                this.updateHoursColumnValue();
              } else {
                var reversedResponse = historyResponse.slice(-3).reverse();
                this.lastClockIn = reversedResponse[0];
                this.lastClockIn.ClockTimeDisplay = moment(this.lastClockIn.ClockTime)
                  .utc()
                  .format(this.dateFormat.toUpperCase() + this.timeFormat);
                this.lastFullClockOut = reversedResponse[1];
                this.lastFullClockOut.ClockTimeDisplay = moment(this.lastFullClockOut.ClockTime)
                  .utc()
                  .format(this.dateFormat.toUpperCase() + this.timeFormat);
                this.lastFullClockIn = reversedResponse[2];
                this.lastFullClockIn.ClockTimeDisplay = moment(this.lastFullClockIn.ClockTime)
                  .utc()
                  .format(this.dateFormat.toUpperCase() + this.timeFormat);
                this.updateHoursColumnValue();
              }
            }
          });
        this.loading = false;
      });
  }

  private updateHoursColumnValue() {
    const startTime = this.lastFullClockIn.ClockTime.substring(6, this.lastFullClockIn.ClockTime.length - 7);
    const stopTime = this.lastFullClockOut.ClockTime.substring(6, this.lastFullClockOut.ClockTime.length - 7);
    const elapsedHours = (stopTime - startTime) / (1000 * 60 * 60.0);
    this.totalHours = parseFloat(elapsedHours.toFixed(2));
  }

  private resetForm() {
    this.performingClockAction = false;
    this.formData.Employee = null;
    this.formData.Password = null;
    this.formData.selectedJob = null;
    this.formData.selectedPhase = null;
    this.formData.lineType = null;
    this.formData.workOrder = null;
    this.formData.scope = null;
    this.formData.payType = null;
    this.formData.mealBreak = null;
    this.scopeList = [];
    this.payTypeList = [];
    this.phases = [];
    this.lineTypes = [];
    this.workOrderList = [];
    this.clockStatus = null;
    this.mealBreak = false;
    this.clear();
    this.clockinoutService.resetMechanicFormData(this.formData);

    if (this.requirePasswordClockIO) {
      this.accountService.getAccountLogoutRedirect();
    }
  }

  private getLocation() {
    this.position = null;
    this.requestingLocation = true;
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition((position: any) => {
        setTimeout(() => {
          this.requestingLocation = false;
          this.geolocationEnabled = true;
          this.position = position;
        });
      });
    } else {
      this.requestingLocation = false;
    }
  }

  disableClockInButton(): boolean {
    return (
      this.performingClockAction ||
      this.formData.PRCo == null ||
      this.formData.Employee == null ||
      //disable if Type is J but job/phase combined is not selected
      (this.formData.lineType == 'J' &&
        !this.clockStatus &&
        this.formData.selectedPhase?.JCJPKeyID == null &&
        !this.tCClockIOIgnoreJobPhase) ||
      //disable if Type is J but job or phase is not selected
      (this.formData.lineType == 'J' && this.formData.selectedJob?.JCJMKeyID != null && this.formData.selectedPhase?.JCJPKeyID == null) ||
      //disable if Type is S but work order/scope/pay type combined is not selected
      (this.formData.lineType == 'S' &&
        (this.formData.workOrder == null || this.formData.scope == null || this.formData.payType == null)) ||
      //disable if type is M and form erequired fields are not selected
      (this.formData.lineType == 'M' && !this.formData.isMechanicformValid)
    );
  }

  canMealBreakClockIn(): boolean {
    if (this.mealBreakSetting[this.employeeInfo.PRCo + ',' + this.employeeInfo.PRGroup] != null) {
      const mealBreakThreshold = parseInt(this.mealBreakSetting[this.employeeInfo.PRCo + ',' + this.employeeInfo.PRGroup]);
      //convert utc clock in time to local time
      const lastClockIn = moment(this.clockMoment.format('YYYY-MM-DD HH:mm:ss'));
      const minutesElapsed = moment().diff(lastClockIn, 'minutes');
      if (minutesElapsed < mealBreakThreshold) return false;
    }
    return true;
  }

  private getCompanies() {
    this.companies = [];
    this.accountService.getCompanies().then((response: any) => {
      if (response != null) {
        this.companies = response;
        if (this.companies.length > 0) {
          this.formData.PRCo = this.companies[0].PRCo;
        }
      }
    });
  }

  private getPhases() {
    this.phases = [];
    this.employeeService
      .getPhasesForClockIn(
        this.formData.selectedJob.JCJMKeyID,
        this.accessToken,
        false,
        encodeURIComponent(this.formData.unlockPass) ?? null
      )
      .then((response: any) => {
        if (response != null) {
          const phases = response;
          phases.forEach((val: any) => {
            if (this.lastUsedJCJPKeyID != null && this.lastUsedJCJPKeyID == val.JCJPKeyID) {
              this.formData.selectedPhase = val;
            }
          });
          this.phases = phases;
        }
      });
  }

  private checkSigned() {
    this.signatureNEmpty = !this.signaturePad.isEmpty();
    this.redBorder = this.signaturePad.isEmpty();
  }

  private setClockStatusResponse(response: any) {
    this.clockStatus = response.ClockAction;
    this.lastUsedJCJMKeyID = response.LastUsedJCJMKeyID;
    this.lastUsedJCJPKeyID = response.LastUsedJCJPKeyID;
    this.clockMoment = moment.utc(response.ClockTime);
    this.lastClockDisplay = this.clockMoment.format('hh:mm A');
    this.mealBreak = response.MealBreak == 'Y';
    this.hadMealBreakToday = response.HadMealBreakToday;

    if (this.formData.lineType == 'J') {
      this.formData.selectedJob = { JCJMKeyID: response.LastUsedJCJMKeyID, JobDisplayName: response.LastUsedJob };
      this.formData.selectedPhase = { JCJPKeyID: response.LastUsedJCJPKeyID, PhaseDescription: response.LastUsedPhase };
    }
    if (this.formData.selectedJob != null) {
      this.getPhases();
    }
    this.getPREHKeyIDByEmployee();
  }

  private showBanner(action: any) {
    const currentTime = moment();
    this.formData.labelFade = false;
    this.lastAction = true;
    this.formData.actionMode = action ? 'success' : 'danger';
    const clockText = action ? ' clocked in at ' : ' clocked out at ';
    this.formData.action =
      'Employee ' + this.formData.Employee + '- ' + this.employeeName + clockText + currentTime.format('hh:mm A') + ' successfully.';
    setTimeout(() => {
      this.formData.labelFade = true;
    }, 5000);
    this.resetForm();
  }

  onMechanicDropdownChange(data: MechanicFormData): void {
    this.clockinoutService.updateMechanicFormData(this.formData, data);
  }
}
